import { useRef, useState } from 'react'
import { Reconciliation, ReconciliationDocumentFileFormat } from '../../../generated'
import { Avatar, Box, CircularProgress, IconButton, Menu, MenuItem } from '@mui/material'
import { PrimaryDots } from '../../assets/images'
import { anchorOrigin, menuPaperStyles, transformOrigin } from '../../theme/styleConstants'

interface ReconciliationsMenuProps {
  reconciliation: Reconciliation
  onUnreconcile: () => void
  onDownload: (fileFormat: ReconciliationDocumentFileFormat) => void
  /** Only the account's latest reconciliation can be reverted; the item is hidden otherwise. */
  canUnreconcile?: boolean
  downloading?: boolean
}

const ReconciliationsMenu = ({
  reconciliation,
  onUnreconcile,
  onDownload,
  canUnreconcile = false,
  downloading = false
}: ReconciliationsMenuProps) => {
  const [open, setOpen] = useState(false)
  const { id } = reconciliation

  const handleClick = () => setOpen(!open)
  const handleClose = () => setOpen(false)

  const anchorRef = {
    [id]: useRef<HTMLButtonElement>(null)
  }

  const handleDownload = (fileFormat: ReconciliationDocumentFileFormat) => {
    handleClose()
    onDownload(fileFormat)
  }

  return (
    <Box>
      <IconButton
        ref={anchorRef[id]}
        id={`long-button-${id}`}
        aria-controls={`long-menu-${id}`}
        aria-haspopup="true"
        onClick={handleClick}
        aria-label="show more"
        disabled={downloading}
      >
        <Avatar sx={{ bgcolor: 'transparent' }}>
          {downloading ? <CircularProgress size={18} /> : <PrimaryDots />}
        </Avatar>
      </IconButton>

      <Menu
        id={`long-menu-${id}`}
        anchorEl={anchorRef[id].current}
        open={open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={handleClose}
        PaperProps={menuPaperStyles}
      >
        <MenuItem onClick={() => handleDownload(ReconciliationDocumentFileFormat.Pdf)}>Download as PDF</MenuItem>

        <MenuItem onClick={() => handleDownload(ReconciliationDocumentFileFormat.Csv)}>Download as .CSV</MenuItem>

        {canUnreconcile && (
          <MenuItem
            onClick={() => {
              handleClose()
              onUnreconcile()
            }}
          >
            Unreconcile
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

export default ReconciliationsMenu
